import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  Wallet, Plus, RefreshCw, ChevronRight, CheckCircle,
  Clock, XCircle, AlertTriangle, Download, Filter, PieChart,
} from "lucide-react";

const STATUS_CONFIG = {
  solicitada: { label: "Solicitada", color: "bg-slate-100 text-slate-700", Icon: Clock },
  aprobada: { label: "Aprobada", color: "bg-green-100 text-green-700", Icon: CheckCircle },
  rechazada: { label: "Rechazada", color: "bg-red-100 text-red-700", Icon: XCircle },
  adelanto_emitido: { label: "Adelanto emitido", color: "bg-blue-100 text-blue-700", Icon: CheckCircle },
  en_viaje: { label: "En viaje", color: "bg-purple-100 text-purple-700", Icon: Clock },
  rendicion_pendiente: { label: "Rendicion pendiente", color: "bg-yellow-100 text-yellow-700", Icon: AlertTriangle },
  rendida: { label: "Rendida", color: "bg-teal-100 text-teal-700", Icon: CheckCircle },
  cerrada: { label: "Cerrada", color: "bg-slate-100 text-slate-500", Icon: CheckCircle },
  cancelada: { label: "Cancelada", color: "bg-red-100 text-red-500", Icon: XCircle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "bg-slate-100 text-slate-600", Icon: Clock };
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

export default function OpsViaticos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [viaticos, setViaticos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterStatus ? `?status=${filterStatus}` : "";
      const res = await api.get(`/ops/viaticos${params}`);
      setViaticos(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async () => {
    try {
      const resp = await api.get("/ops/viaticos/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = "viaticos.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  const summaryByStatus = {};
  viaticos.forEach(v => { summaryByStatus[v.status] = (summaryByStatus[v.status] || 0) + 1; });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="ops-viaticos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-yellow-500" /> Viaticos
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Solicitudes de viaticos — de la solicitud al cierre con rendicion</p>
        </div>
        <div className="flex gap-2">
          {["corporate_admin", "treasury", "branch_manager", "hr_admin"].includes(user?.role) && (
            <button
              onClick={() => navigate("/ops/viaticos/nuevo")}
              className="flex items-center gap-2 px-4 py-2 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors"
              data-testid="btn-nuevo-viatico">
              <Plus className="w-4 h-4" /> Nueva solicitud
            </button>
          )}
          <button onClick={handleExport} className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" title="Exportar Excel">
            <Download className="w-4 h-4" />
          </button>
          {["corporate_admin", "treasury", "hr_admin"].includes(user?.role) && (
            <button onClick={() => navigate("/ops/viaticos/reportes")}
              className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" title="Reportes agregados"
              data-testid="btn-viaticos-reportes">
              <PieChart className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Quick filters */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilterStatus("")}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!filterStatus ? "bg-[#FFDF00] text-slate-900" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          Todos ({viaticos.length})
        </button>
        {Object.entries(STATUS_CONFIG).map(([s, cfg]) => {
          const count = summaryByStatus[s] || 0;
          if (count === 0) return null;
          return (
            <button key={s} onClick={() => setFilterStatus(filterStatus === s ? "" : s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filterStatus === s ? cfg.color : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {cfg.label} ({count})
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : viaticos.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <Wallet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="font-semibold text-slate-700">No hay solicitudes de viaticos</p>
          <p className="text-sm text-slate-500 mt-1 mb-4">
            {filterStatus ? "No hay solicitudes con ese estado." : "Crea la primera solicitud para comenzar a gestionar viaticos del equipo."}
          </p>
          {!filterStatus && ["corporate_admin", "treasury", "branch_manager", "hr_admin"].includes(user?.role) && (
            <button onClick={() => navigate("/ops/viaticos/nuevo")}
              className="px-6 py-2.5 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors">
              Crear solicitud
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {viaticos.filter(v => !filterStatus || v.status === filterStatus).map(v => (
            <div
              key={v.id}
              onClick={() => navigate(`/ops/viaticos/${v.id}`)}
              className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors"
              data-testid={`viatico-row-${v.id}`}
            >
              <div className="flex items-center gap-4">
                <div>
                  <div className="font-semibold text-slate-900 font-mono text-sm">{v.request_number}</div>
                  {/* Fix WH28 (2026-09-04): la fila no decia de quien era la
                      solicitud, asi que habia que abrir una por una para saberlo. */}
                  <div className="text-xs font-medium text-slate-700">{v.requester_name || v.requester_email || "—"}</div>
                  <div className="text-xs text-slate-500">{v.destination_description}</div>
                  <div className="text-xs text-slate-400">{v.trip_start} → {v.trip_end}</div>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right text-sm">
                  <div className="text-xs text-slate-500">Solicitado</div>
                  <div className="font-bold">${(v.total_requested || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}</div>
                </div>
                {v.advance_amount && (
                  <div className="text-right text-sm">
                    <div className="text-xs text-slate-500">Adelanto</div>
                    <div className="font-semibold text-blue-600">${(v.advance_amount || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}</div>
                  </div>
                )}
                <StatusBadge status={v.status} />
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
